import { useEffect, useState } from "react";
import Shell from "../../components/Shell";
import api, { formatApiError } from "../../lib/api";
import { StatusPill } from "../clinic/Dashboard";
import { toast } from "sonner";

const STATUSES = ["pending", "processing", "completed", "cancelled"];

export default function AdminOrders() {
    const [list, setList] = useState([]);
    const [open, setOpen] = useState(null);
    const [notes, setNotes] = useState("");
    const [status, setStatus] = useState("pending");

    const reload = async () => { const { data } = await api.get("/admin/orders"); setList(data || []); };
    useEffect(() => { reload(); }, []);

    const openOrder = (o) => { setOpen(o); setNotes(o.admin_notes || ""); setStatus(o.status); };
    const save = async () => {
        try {
            await api.put(`/admin/orders/${open.id}`, { status, admin_notes: notes });
            toast.success("Order updated");
            setOpen(null); reload();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <Shell variant="admin">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10 animate-fade-in">
                <p className="eyebrow mb-2">Operations</p>
                <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight mb-8">Orders</h1>

                <div className="card-base overflow-hidden" data-testid="orders-table">
                    <table className="w-full text-left">
                        <thead className="bg-zinc-50/60 border-b border-zinc-200">
                            <tr className="text-[10px] uppercase tracking-widest text-zinc-500">
                                <th className="py-3 px-4">Order</th><th className="py-3 px-4">Clinic</th><th className="py-3 px-4">Items</th><th className="py-3 px-4">Total</th><th className="py-3 px-4">Status</th><th className="py-3 px-4">Submitted</th><th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {list.map(o => (
                                <tr key={o.id} className="border-b border-zinc-100">
                                    <td className="py-3 px-4 text-sm font-medium">{o.order_no}</td>
                                    <td className="py-3 px-4 text-sm">{o.clinic_name}<br /><span className="text-xs text-zinc-500">{o.email}</span></td>
                                    <td className="py-3 px-4 text-sm">{o.items.length}</td>
                                    <td className="py-3 px-4 text-sm">${o.total.toFixed(2)}</td>
                                    <td className="py-3 px-4"><StatusPill status={o.status} /></td>
                                    <td className="py-3 px-4 text-xs text-zinc-500">{new Date(o.created_at).toLocaleDateString()}</td>
                                    <td className="py-3 px-4 text-right"><button data-testid={`order-open-${o.id}`} onClick={() => openOrder(o)} className="btn-ghost">Open →</button></td>
                                </tr>
                            ))}
                            {list.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-sm text-zinc-500">No orders yet.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>

            {open && (
                <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={() => setOpen(null)}>
                    <div className="bg-white w-full max-w-2xl h-full overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
                        <p className="eyebrow mb-1">Order</p>
                        <h2 className="font-heading text-2xl mb-2">{open.order_no}</h2>
                        <p className="text-sm text-zinc-500 mb-6">{open.clinic_name} · {open.contact_name} · {open.email}</p>

                        <table className="w-full text-left text-sm mb-6 border-t border-b border-zinc-200">
                            <thead><tr className="text-[10px] uppercase tracking-widest text-zinc-500">
                                <th className="py-2">Product</th><th className="py-2">SKU</th><th className="py-2">Qty</th><th className="py-2 text-right">Subtotal</th>
                            </tr></thead>
                            <tbody>
                                {open.items.map(it => (
                                    <tr key={it.product_id} className="border-t border-zinc-100">
                                        <td className="py-2">{it.name}</td><td className="py-2 text-zinc-500">{it.sku}</td><td className="py-2">{it.quantity}</td><td className="py-2 text-right">${it.subtotal.toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {open.clinic_notes && <p className="text-sm mb-4"><span className="eyebrow mr-2">Clinic note</span>{open.clinic_notes}</p>}

                        <div>
                            <label className="input-label">Status</label>
                            <select data-testid="order-status" value={status} onChange={(e) => setStatus(e.target.value)} className="input-base">
                                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                        <div className="mt-4">
                            <label className="input-label">Admin notes (visible to clinic)</label>
                            <textarea data-testid="order-admin-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="input-base min-h-[100px]" />
                        </div>

                        <div className="flex gap-3 mt-6">
                            <button data-testid="order-save" onClick={save} className="btn-primary">Save</button>
                            <button onClick={() => setOpen(null)} className="btn-ghost ml-auto">Close</button>
                        </div>
                    </div>
                </div>
            )}
        </Shell>
    );
}
